// Importar interfaces de QuoteParameters para reutilizar tipos
import { IAssetsValue, IAccessory, PaymentType } from './iquote-parameters.interface';

// ===== ENUMS ADICIONALES =====
export enum AmortizationPeriodStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE'
}

// ===== INTERFACES AUXILIARES PARA QUOTE CALCULADA =====
export interface IAdvanceDetail {
  rate: number;
  percentage: number;
  amount: number;
  iva: number;
  total: number;
}

export interface IOpeningCommissionDetail {
  rate: number;
  percentage: number;
  amount: number;
  iva: number;
  total: number;
}

export interface IRentDetail {
  amount: number;
  accessories: number;
  iva: number;
  total: number;
}

export interface IResiduaryDetail {
  percentage: number;
  amount: number;
  iva: number;
  total: number;
  isPaid?: boolean;
  appliedAmount?: number;
  applicationId?: string;
}

export interface IDownPaymentDetail {
  amount: number;
  iva: number;
  total: number;
  isPaid?: boolean;
  appliedAmount?: number;
  applicationId?: string;
}

export interface IAdvancedRentsDetail {
  number: number;
  amount: number;
  // F-016: mensualidad de los accesorios financiados de las letras anticipadas. Solo existe en cotizaciones con
  // `advancedRentsIncludeAccessories`; ya va sumada en `downPayment.amount`.
  accessories?: number;
  iva: number;
  total: number;
}

export interface ICashDTO {
  amount: number;
  iva: number;
  total: number;
  concepts: string | null;
}

export interface IFinancedDTO {
  period: {
    amount: number;
    interest: number;
    iva: number;
    total: number;
  };
  total: {
    amount: number;
    interest: number;
    iva: number;
    total: number;
    concepts: string;
  };
}

export interface IAccessoriesDetail {
  cash: ICashDTO;
  financed: IFinancedDTO;
}

export interface ITermData {
  term: number;
  irr: number;
  irrAdjusted: number;
  leaseFactor: number;
  taxSavingsIncomeDeductibility: number;
  commercialRate: number;
  totalLeasePayment: number;
  rent: IRentDetail;
  residuary: IResiduaryDetail;
  downPayment: IDownPaymentDetail;
  advancedRents: IAdvancedRentsDetail;
  accessoriesDetail: IAccessoriesDetail;
}

export interface IAppliedAmounts {
  capital: number;
  interest: number;
  accessoriesAmount: number;
  accessoriesInterest: number;
  inputIva: number;
  moraAmount: number;
}

export interface IAmortizationPeriod {
  _id?: string;
  date: Date | string;
  period: number;
  interest: number;
  capital: number;
  accessoriesAmount: number;
  accessoriesInterest: number;
  rent: number;
  payment: number;
  appliedPayment: number;
  appliedAmounts: IAppliedAmounts;
  outstandingBalance: number;
  balanceOfPaymentPeriod: number;
  favorableBalance: number;
  moraRate: number;
  moraDaysAccumulated: number;
  moraAmount: number;
  moraDaily: number;
  inputIva: number;
  outputIva: number;
  totalInputs: number;
  totalOutputs: number;
  cashFlow: number;
  cashFlowAdjusted: number;
  status: AmortizationPeriodStatus;
  createdAt: Date;
  updatedAt: Date;
}

// ===== INTERFACE PRINCIPAL QUOTE CALCULADA =====
export interface IQuote {
  // Identificación y datos básicos
  _id?: string;
  user: any;
  client?: any;
  assets: any;
  assetsNames: string;
  assetsValue: IAssetsValue;
  
  // Detalles financieros calculados
  advancedRents: number;
  advance: IAdvanceDetail;
  openingCommission: IOpeningCommissionDetail;
  
  // Configuración
  leaseType: string;
  
  // Término seleccionado con cálculos completos
  termData: ITermData;
  
  // Tabla de amortización y otros cálculos
  amortizationPeriods: string[] | IAmortizationPeriod[];
  amountToFinance: number;
  interestRate: number;
  provisionPe: number;
  ratificationExpenses: number;
  
  // Accesorios
  accessories?: string[] | IAccessory[];
  accessoriesConcepts: string;
  
  // Estado y fechas
  status: 'Activa' | 'Cancelada';
  createdAt: Date;
  expiresAt: Date;
  updatedAt: Date;
  deletedAt?: Date | null;
}

// ===== INTERFACES PARA RESPUESTAS DEL API =====
export interface IQuoteResponse {
  error: boolean;
  message: string;
  data: IQuote;
}

export interface IQuoteListResponse {
  error: boolean;
  message: string;
  data: IQuote[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ===== INTERFACE PARA RESPUESTA DE LISTA (COMPATIBLE CON ESTRUCTURA ACTUAL) =====
export interface IQuoteListApiResponse {
  error: boolean;
  msg: string;
  data: {
    data: IQuote[];
    total: number;
  };
  status: number;
  page: number;
  itemsByPage: number;
  totalItems: number;
}

// ===== INTERFACE PARA LISTA (TABLA) =====
export interface IQuoteList {
  _id?: string;
  clientName?: string;
  assetsNames: string;
  assetsValue: IAssetsValue;
  leaseType: string;
  term: number;
  rentTotal: number;
  totalLeasePayment: number;
  accessoriesConcepts: string;
  status: 'Activa' | 'Cancelada';
  createdAt: Date;
  expiresAt: Date;
}

// ===== ALIASES PARA COMPATIBILIDAD =====
export interface CashDTO extends ICashDTO {}
export interface FinancedDTO extends IFinancedDTO {}
